import React, { useState, useEffect, useRef } from 'react';
import CollapsibleSection from './CollapsibleSection';
import HtmlContent from './HtmlContent';

const ApplicantSectionTabs = ({ applicantData, applicantName }) => {
  const [openSections, setOpenSections] = useState(new Set());
  const iframeRef = useRef(null);

  // Auto-expand first section when applicant changes
  useEffect(() => {
    if (applicantData && typeof applicantData === 'object') {
      const sections = Object.keys(applicantData);
      if (sections.length > 0) {
        setOpenSections(new Set([sections[0]]));
      }
    }
  }, [applicantData, applicantName]);

  // Process HTML for iframe when it's Commercial CIBIL (has scripts)
  const processedHtml = React.useMemo(() => {
    if (!applicantData || typeof applicantData !== 'string') return null;

    // Check if this looks like Commercial CIBIL content
    const isCreditReport = applicantData.includes('credit-report.js') || 
                          applicantData.includes('renderCommercialCibil') ||
                          applicantData.includes('COMMERCIAL_CIBIL_DATA');

    if (!isCreditReport) return null;

    console.log('[CREDIT-REPORT] Processing Commercial CIBIL HTML for iframe');

    try {
      // Fix relative paths to absolute URLs
      const origin = window.location.origin;
      console.log('[CREDIT-REPORT] Using origin:', origin);
      
      let fixedHtml = applicantData;
      
      // Fix script src paths - catch both /static/ and static/ (without leading slash)
      fixedHtml = fixedHtml.replace(
        /src=(["'])(\/?static\/[^"']+)\1/g,
        (match, quote, path) => {
          const cleanPath = path.startsWith('/') ? path : '/' + path;
          return `src=${quote}${origin}${cleanPath}${quote}`;
        }
      );
      
      // Fix link href paths - catch both /static/ and static/ (without leading slash)
      fixedHtml = fixedHtml.replace(
        /href=(["'])(\/?static\/[^"']+)\1/g,
        (match, quote, path) => {
          const cleanPath = path.startsWith('/') ? path : '/' + path;
          return `href=${quote}${origin}${cleanPath}${quote}`;
        }
      );
      
      console.log('[CREDIT-REPORT] ✅ Fixed relative paths to absolute URLs');
      return fixedHtml;
    } catch (error) {
      console.error('[CREDIT-REPORT] ❌ Error processing HTML:', error);
      return null;
    }
  }, [applicantData]);

  if (!applicantData) {
    return (
      <div className="content-empty">
        <div className="content-empty-box">
          <p style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '8px' }}>Select an Applicant</p>
          <p style={{ fontSize: '0.875rem' }}>Choose an applicant tab above to view their information</p>
        </div>
      </div>
    );
  }

  // If applicantData is a string, check if it's Commercial CIBIL (needs iframe) or regular HTML
  if (typeof applicantData === 'string') {
    const isCreditReport = applicantData.includes('credit-report.js') || 
                          applicantData.includes('renderCommercialCibil') ||
                          applicantData.includes('COMMERCIAL_CIBIL_DATA');

    if (isCreditReport && processedHtml) {
      // Render Commercial CIBIL in iframe using srcDoc
      return (
        <div className="content-padding">
          <div
            style={{
              width: '100%',
              height: '800px',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
            }}
          >
            <iframe
              ref={iframeRef}
              srcDoc={processedHtml}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '4px',
              }}
              title="Commercial CIBIL Credit Report"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-downloads"
              onLoad={() => {
                console.log('[CREDIT-REPORT] Iframe content loaded');
                // Auto-size the iframe to its content height so the report
                // flows in the page (matching the pre-sandbox behavior) instead
                // of showing a nested scrollbar inside the iframe.
                try {
                  const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
                  if (!doc) return;
                  const body = doc.body;
                  const htmlEl = doc.documentElement;
                  if (!body) return;
                  const contentHeight = Math.max(
                    body.scrollHeight,
                    body.offsetHeight,
                    htmlEl.scrollHeight,
                    htmlEl.offsetHeight
                  );
                  if (contentHeight > 0 && iframeRef.current) {
                    iframeRef.current.style.height = `${contentHeight}px`;
                  }
                } catch (e) {
                  console.warn('[CREDIT-REPORT] Could not auto-size iframe:', e);
                }
              }}
              onError={(e) => console.error('[CREDIT-REPORT] Iframe error:', e)}
            />
          </div>
        </div>
      );
    }

    // Regular HTML content - render via HtmlContent so any embedded
    // <script> tags (e.g. the Office FI / Residence FI tab-switcher) are
    // actually executed instead of staying inert as they would with
    // dangerouslySetInnerHTML.
    return (
      <div className="content-padding">
        <HtmlContent className="prose" html={applicantData} />
      </div>
    );
  }

  // If applicantData is an object, render sections
  const sections = Object.keys(applicantData);

  return (
    <div className="applicant-sections">
      <div className="sections-header">
        <h3 className="applicant-name">{applicantName}</h3>
        <p className="sections-count">{sections.length} section{sections.length !== 1 ? 's' : ''}</p>
      </div>
      
      <div className="sections-container">
        {sections.map((sectionKey, index) => {
          // Check if this is the "brief" section (case-insensitive)
          const isBriefSection = sectionKey.toLowerCase().includes('brief');
          // Open brief section by default, or first section if no brief section exists
          const shouldBeOpen = isBriefSection || (index === 0 && !sections.some(key => key.toLowerCase().includes('brief')));
          
          return (
            <CollapsibleSection
              key={sectionKey}
              title={sectionKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()}
              content={applicantData[sectionKey]}
              isDefaultOpen={shouldBeOpen}
              sectionKey={sectionKey}
            />
          );
        })}
      </div>
    </div>
  );
};

export default ApplicantSectionTabs;
